import { useCallback } from 'react';
import logger from '~/services/logger';
import { twMerge } from 'tailwind-merge';
import type { FeedEntry } from '~/services/layout/index.ts';
import { ResponsiveImage } from './responsive-image';
import { type UseFavoriteActionReturnType } from '~/hooks/use-favorite-action.ts';
import {
  getCellVisibilityStatus,
  getFavoriteActionIconSources,
  getPopupMenuActionIconSources,
  type ResponsiveSources,
} from './power-cell-getters';
import { isPopupMenuActionIdentifier } from '~/services/plugins/popup-menu/components/utils/popup-menu-action-identifier.ts';
import type { PopupMenuTriggerHandler } from '~/services/plugins/popup-menu/components/host/index.ts';
import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_index';

/**
 * ActionIcon component - renders the favorite icon for a cell
 * @param assets cell assets for each breakpoint
 * @param entry feed entry
 * @param favoriteActionData favourite state and toggle of the entry
 * @param actionIdentifier cell action identifier for the current breakpoint
 * @param onPopupMenuTrigger emits a Popup Trigger; set when the icon opens a Popup Menu
 * @returns JSX.Element
 */
export function ActionIconV2({
  assets,
  entry,
  favoriteActionData,
  actionIdentifier,
  onPopupMenuTrigger,
}: {
  assets: any;
  entry: FeedEntry;
  favoriteActionData: UseFavoriteActionReturnType;
  actionIdentifier?: string;
  onPopupMenuTrigger?: PopupMenuTriggerHandler;
}): JSX.Element {
  const {
    isFavorite,
    favoriteAction,
    shouldRenderFavoriteAction,
  }: UseFavoriteActionReturnType = favoriteActionData;

  const { defaultThemeV2Config, popupMenuConfig } =
    useLoaderData<typeof loader>();

  const shouldRenderPopupMenuAction: boolean =
    isPopupMenuActionIdentifier(actionIdentifier) &&
    Boolean(onPopupMenuTrigger);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      favoriteAction();
    },
    [favoriteAction]
  );

  const handlePopupTriggerClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      onPopupMenuTrigger?.({
        triggerElement: event.currentTarget,
        source: 'click',
      });
    },
    [onPopupMenuTrigger]
  );

  const handlePopupTriggerContextMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      onPopupMenuTrigger?.({
        triggerElement: event.currentTarget,
        source: 'contextmenu',
        pointer: { x: event.clientX, y: event.clientY },
      });
    },
    [onPopupMenuTrigger]
  );

  if (!shouldRenderFavoriteAction && !shouldRenderPopupMenuAction) return <></>;

  const imageSources: ResponsiveSources = shouldRenderPopupMenuAction
    ? getPopupMenuActionIconSources({
        assets,
        popupMenuCustomConfiguration: popupMenuConfig?.customConfiguration,
      })
    : getFavoriteActionIconSources({
        assets,
        isFavorite,
        defaultThemeV2Config,
      });

  const isVisible = getCellVisibilityStatus(imageSources);

  if (!isVisible) {
    return <></>;
  }

  try {
    return (
      <button
        onClick={
          shouldRenderPopupMenuAction ? handlePopupTriggerClick : handleClick
        }
        onContextMenu={
          shouldRenderPopupMenuAction
            ? handlePopupTriggerContextMenu
            : undefined
        }
        type="button"
        className={twMerge(
          'pointer-events-auto absolute z-40 mb-cell-action-icon-b ml-cell-action-icon-l mr-cell-action-icon-r mt-cell-action-icon-t opacity-100 action-icon-pos'
        )}
        data-testid={`action-icon-V2-${entry.id}`}
        key={entry.id}
      >
        <ResponsiveImage
          imageSources={imageSources}
          altText={
            entry?.title &&
            `${entry?.title} ${
              shouldRenderPopupMenuAction ? 'Action Icon' : 'Favorite Icon'
            }`
          }
          classNames="h-cell-action-icon w-cell-action-icon"
        />
      </button>
    );
  } catch (error: any) {
    logger.info(`ActionIconV2: ${error.message}`);
    return <></>;
  }
}
